import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBusiness } from '../context/BusinessContext';
import { useAuth } from '../context/AuthContext';
import { useModal } from '../context/ModalContext';
import DashboardLayout from '../components/DashboardLayout';
import { api } from '../api/client';

interface Photo {
  id: string;
  url: string;
  name: string;
  courtId?: string;
  isMain: boolean;
}

type SectionKey = 'business' | 'location' | 'hours' | 'photos';

// Section configuration with icons
const SECTION_CONFIG: Record<SectionKey, { title: string; icon: JSX.Element }> = {
  business: {
    title: 'Business Information',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
      </svg>
    ),
  },
  location: {
    title: 'Location & Address',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
        <path d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
    ),
  },
  hours: {
    title: 'Operating Hours',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <circle cx="12" cy="12" r="10" />
        <path d="M12 6v6l4 2" />
      </svg>
    ),
  },
  photos: {
    title: 'Photos & Gallery',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <rect x="3" y="3" width="18" height="18" rx="2" />
        <circle cx="8.5" cy="8.5" r="1.5" />
        <path d="M21 15l-5-5L5 21" />
      </svg>
    ),
  },
};

const DAYS_OF_WEEK = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

export default function FacilitySetupScreen() {
  const {
    business,
    updateBusiness,
    saveBusiness,
    publishBusiness,
    unpublishBusiness,
    loading,
    needsVenueSelection,
  } = useBusiness();
  const { isSuperAdmin } = useAuth();
  const { showAlert, showDanger } = useModal();
  const navigate = useNavigate();

  // Section expansion state
  const [expandedSections, setExpandedSections] = useState<Set<SectionKey>>(new Set<SectionKey>(['business']));

  // Photos state
  const fileInputRef = useRef<HTMLInputElement>(null);
  const waiverInputRef = useRef<HTMLInputElement>(null);
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [uploading, setUploading] = useState(false);
  const [selectedPhotoTarget, setSelectedPhotoTarget] = useState<string>('facility');
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const [waiverUploading, setWaiverUploading] = useState(false);
  const [waiverUploadError, setWaiverUploadError] = useState<string | null>(null);

  // Saving state
  const [saving, setSaving] = useState(false);

  // Load photos from venue config on mount
  useEffect(() => {
    if (business && business.photos && business.photos.length > 0) {
      setPhotos(business.photos.map((p) => ({
        id: p.id || `photo-${Date.now()}-${Math.random()}`,
        url: p.url,
        name: p.caption || 'Facility Photo',
        isMain: p.isMain || false,
      })));
    }
  }, [business?.id, business?.photos?.length]);

  // Redirect SUPERADMIN to venue selection if needed
  useEffect(() => {
    if (needsVenueSelection && isSuperAdmin) {
      navigate('/superadmin');
    }
  }, [needsVenueSelection, isSuperAdmin, navigate]);

  const sectionCompletion: Record<SectionKey, boolean> = {
    business: !!(business?.businessName && business.email && business.phone && business.description),
    location: !!(business?.location.address && business.location.city && business.location.state && business.location.zipCode),
    hours: (business?.operatingHours.length || 0) > 0,
    photos: photos.length > 0,
  };

  const isSectionComplete = (section: SectionKey): boolean => {
    return sectionCompletion[section];
  };

  useEffect(() => {
    if (!business || business.isPublished) return;
    setExpandedSections(prev => {
      const next = new Set(Array.from(prev).filter(section => !sectionCompletion[section]));
      return next.size === prev.size ? prev : next;
    });
  }, [
    business,
    business?.isPublished,
    sectionCompletion.business,
    sectionCompletion.location,
    sectionCompletion.hours,
    sectionCompletion.photos,
  ]);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="loading" style={{ minHeight: '400px' }}>
          <div className="spinner"></div>
        </div>
      </DashboardLayout>
    );
  }

  if (needsVenueSelection) {
    return (
      <DashboardLayout>
        <div className="page-header">
          <h1>Select a Venue</h1>
          <p>As a Super Admin, please select a venue to manage from the All Venues page.</p>
        </div>
        <button className="btn btn-primary" onClick={() => navigate('/superadmin')}>
          Go to All Venues
        </button>
      </DashboardLayout>
    );
  }

  if (!business) {
    return (
      <DashboardLayout>
        <div className="loading" style={{ minHeight: '400px' }}>
          <div className="spinner"></div>
        </div>
      </DashboardLayout>
    );
  }

  const toggleSection = (section: SectionKey) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(section)) {
      newExpanded.delete(section);
    } else {
      newExpanded.add(section);
    }
    setExpandedSections(newExpanded);
  };

  const getSectionProgress = (): { complete: number; total: number } => {
    const sections: SectionKey[] = ['business', 'location', 'hours', 'photos'];
    const complete = sections.filter(s => isSectionComplete(s)).length;
    return { complete, total: sections.length };
  };

  const handleSaveAll = async () => {
    setSaving(true);
    try {
      await saveBusiness();
      await showAlert('Saved', 'All changes have been saved successfully.', 'success');
    } catch (err) {
      await showAlert('Error', err instanceof Error ? err.message : 'Failed to save changes', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async () => {
    try {
      await publishBusiness();
      await showAlert('Published', 'Your facility is now discoverable on PickleCourts.', 'success');
    } catch (err) {
      await showAlert('Error', err instanceof Error ? err.message : 'Failed to publish', 'error');
    }
  };

  const handleUnpublish = async () => {
    try {
      await unpublishBusiness();
      await showAlert('Unpublished', 'Your facility is no longer discoverable on PickleCourts.', 'info');
    } catch (err) {
      await showAlert('Error', err instanceof Error ? err.message : 'Failed to unpublish', 'error');
    }
  };

  // Photo handlers
  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;
    await uploadFiles(files);
  };

  const handleDrop = async (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragActive(false);
    const files = event.dataTransfer.files;
    if (files && files.length > 0) {
      await uploadFiles(files);
    }
  };

  const uploadFiles = async (files: FileList) => {
    setUploading(true);
    setUploadError(null);

    const newPhotos: Photo[] = [];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];

      try {
        const folder = selectedPhotoTarget === 'facility' ? 'venue' : 'courts';
        const { uploadUrl, publicUrl } = await api.getPhotoUploadUrl(
          file.name,
          file.type,
          folder as 'venue' | 'courts'
        );

        await api.uploadPhotoToS3(uploadUrl, file);

        newPhotos.push({
          id: `photo-${Date.now()}-${i}`,
          url: publicUrl,
          name: file.name,
          courtId: selectedPhotoTarget === 'facility' ? undefined : selectedPhotoTarget,
          isMain: photos.length === 0 && i === 0,
        });
      } catch (err) {
        console.error('Upload error:', err);
        setUploadError(err instanceof Error ? err.message : 'Failed to upload photo');
      }
    }

    if (newPhotos.length > 0) {
      const updatedPhotos = [...photos, ...newPhotos];
      setPhotos(updatedPhotos);
      await savePhotosToConfig(updatedPhotos);
    }

    setUploading(false);

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const savePhotosToConfig = async (photoList: Photo[]) => {
    try {
      const venuePhotos = photoList.filter(p => !p.courtId).map(p => ({
        id: p.id,
        url: p.url,
        caption: p.name,
        isMain: p.isMain,
      }));

      await api.updateVenue({
        config: {
          photos: venuePhotos,
        },
      });
    } catch (err) {
      console.error('Failed to save photos to config:', err);
    }
  };

  const handleDeletePhoto = async (photoId: string) => {
    const confirmed = await showDanger(
      'Delete Photo',
      'Are you sure you want to delete this photo? This action cannot be undone.',
      'Delete'
    );

    if (!confirmed) return;

    const photo = photos.find(p => p.id === photoId);
    if (!photo) return;

    try {
      if (photo.url.includes('s3.amazonaws.com')) {
        await api.deletePhoto(photo.url);
      }

      const updatedPhotos = photos.filter((p) => p.id !== photoId);
      setPhotos(updatedPhotos);
      await savePhotosToConfig(updatedPhotos);
    } catch (err) {
      console.error('Failed to delete photo:', err);
      await showAlert('Error', 'Failed to delete photo. Please try again.', 'error');
    }
  };

  const handleSetMainPhoto = async (photoId: string) => {
    const updatedPhotos = photos.map((p) => ({ ...p, isMain: p.id === photoId }));
    setPhotos(updatedPhotos);
    await savePhotosToConfig(updatedPhotos);
  };

  const handleWaiverFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setWaiverUploadError(null);

    if (file.type !== 'application/pdf') {
      setWaiverUploadError('Waiver must be a PDF file.');
      event.target.value = '';
      return;
    }

    if (file.size > 15 * 1024 * 1024) {
      setWaiverUploadError('Waiver file is too large. Maximum size is 15MB.');
      event.target.value = '';
      return;
    }

    setWaiverUploading(true);
    try {
      const { uploadUrl, publicUrl } = await api.getPhotoUploadUrl(file.name, file.type, 'waivers');
      await api.uploadPhotoToS3(uploadUrl, file);

      // Keep the prior source PDF: settings may not be saved yet, and an
      // in-flight checkout can still be reviewing it.
      updateBusiness({
        waiverPdf: {
          enabled: true,
          url: publicUrl,
          fileName: file.name,
          sha256: null, // Backend recomputes from S3 bytes on save.
          uploadedAt: new Date().toISOString(),
        },
      });
    } catch (err) {
      setWaiverUploadError(err instanceof Error ? err.message : 'Failed to upload waiver PDF');
    } finally {
      setWaiverUploading(false);
      if (waiverInputRef.current) {
        waiverInputRef.current.value = '';
      }
    }
  };

  const handleRemoveWaiver = async () => {
    if (!business.waiverPdf?.url) return;

    const confirmed = await showDanger(
      'Remove Waiver',
      'Are you sure you want to remove the waiver PDF from checkout?',
      'Remove'
    );

    if (!confirmed) return;

    try {
      updateBusiness({
        waiverPdf: null,
        ...(business.waiverPolicy?.method === 'checkout' ? { waiverPolicy: { method: 'unknown' as const, evidence: '', instructions: '' } } : {}),
      });
      setWaiverUploadError(null);
    } catch (err) {
      setWaiverUploadError(err instanceof Error ? err.message : 'Failed to remove waiver');
    }
  };

  const facilityPhotos = photos.filter((p) => !p.courtId);
  const progress = getSectionProgress();
  const isLocked = business.isPublished;

  return (
    <DashboardLayout>
      {/* Header with Progress */}
      <div className="facility-setup-header">
        <div className="facility-setup-header__content">
          <h1 className="facility-setup-header__title">Facility Setup</h1>
          <p className="facility-setup-header__subtitle">
            Complete your profile to start accepting bookings
          </p>
        </div>
        <div className="facility-setup-header__actions">
          <div className="facility-setup-header__progress">
            <div className="facility-setup-header__progress-bar">
              <div
                className="facility-setup-header__progress-fill"
                style={{ width: `${(progress.complete / progress.total) * 100}%` }}
              />
            </div>
            <span className="facility-setup-header__progress-text">
              {progress.complete} of {progress.total} complete
            </span>
          </div>
          {business.isPublished ? (
            <button className="facility-setup-header__btn facility-setup-header__btn--secondary" onClick={handleUnpublish}>
              <svg viewBox="0 0 20 20" fill="currentColor" width="16" height="16">
                <path fillRule="evenodd" d="M3.707 2.293a1 1 0 00-1.414 1.414l14 14a1 1 0 001.414-1.414l-1.473-1.473A10.014 10.014 0 0019.542 10C18.268 5.943 14.478 3 10 3a9.958 9.958 0 00-4.512 1.074l-1.78-1.781zm4.261 4.26l1.514 1.515a2.003 2.003 0 012.45 2.45l1.514 1.514a4 4 0 00-5.478-5.478z" clipRule="evenodd" />
                <path d="M12.454 16.697L9.75 13.992a4 4 0 01-3.742-3.741L2.335 6.578A9.98 9.98 0 00.458 10c1.274 4.057 5.065 7 9.542 7 .847 0 1.669-.105 2.454-.303z" />
              </svg>
              Unpublish
            </button>
          ) : (
            <button className="facility-setup-header__btn facility-setup-header__btn--primary" onClick={handlePublish}>
              <svg viewBox="0 0 20 20" fill="currentColor" width="16" height="16">
                <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
              </svg>
              Make Discoverable
            </button>
          )}
        </div>
      </div>

      {/* Status Banner */}
      <div className={`facility-status-banner ${business.isPublished ? 'facility-status-banner--published' : 'facility-status-banner--draft'}`}>
        <div className="facility-status-banner__icon">
          {business.isPublished ? (
            <svg viewBox="0 0 20 20" fill="currentColor" width="20" height="20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
          ) : (
            <svg viewBox="0 0 20 20" fill="currentColor" width="20" height="20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
          )}
        </div>
        <div className="facility-status-banner__content">
          <span className="facility-status-banner__title">
            {business.isPublished ? 'Discoverable on PickleCourts' : 'Draft Mode'}
          </span>
          <span className="facility-status-banner__description">
            {business.isPublished
              ? 'Customers can find and book your facility on PickleCourts'
              : 'Complete your setup to make your facility discoverable'}
          </span>
        </div>
      </div>

      {/* Sections */}
      <div className="facility-sections">
        {/* Business Information Section */}
        <div className={`facility-section ${expandedSections.has('business') ? 'facility-section--expanded' : ''}`}>
          <button className="facility-section__header" onClick={() => toggleSection('business')}>
            <div className="facility-section__header-left">
              <div className={`facility-section__icon ${isSectionComplete('business') ? 'facility-section__icon--complete' : ''}`}>
                {SECTION_CONFIG.business.icon}
              </div>
              <div className="facility-section__title-area">
                <h3 className="facility-section__title">{SECTION_CONFIG.business.title}</h3>
              </div>
            </div>
            <div className="facility-section__header-right">
              <span className={`facility-section__status ${isSectionComplete('business') ? 'facility-section__status--complete' : ''}`}>
                {isSectionComplete('business') ? (
                  <>
                    <svg viewBox="0 0 20 20" fill="currentColor" width="14" height="14">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    Complete
                  </>
                ) : 'Incomplete'}
              </span>
              <span className={`facility-section__chevron ${expandedSections.has('business') ? 'facility-section__chevron--expanded' : ''}`}>
                <svg viewBox="0 0 20 20" fill="currentColor" width="20" height="20">
                  <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </span>
            </div>
          </button>
          {expandedSections.has('business') && (
            <div className="facility-section__content">
              <div className="facility-form-grid">
                <div className={`facility-form-group facility-form-group--half ${isLocked ? 'facility-form-group--locked' : ''}`}>
                  <label className="facility-form-label">
                    Business Name
                    <span className="facility-form-label__required">*</span>
                  </label>
                  <input
                    type="text"
                    className="facility-form-input"
                    value={business.businessName}
                    onChange={(e) => updateBusiness({ businessName: e.target.value })}
                    placeholder="Sterling Pickleball Center"
                    readOnly={isLocked}
                  />
                </div>
                <div className={`facility-form-group facility-form-group--half ${isLocked ? 'facility-form-group--locked' : ''}`}>
                  <label className="facility-form-label">
                    Business Email
                    <span className="facility-form-label__required">*</span>
                  </label>
                  <input
                    type="email"
                    className="facility-form-input"
                    value={business.email}
                    onChange={(e) => updateBusiness({ email: e.target.value })}
                    placeholder="info@yourbusiness.com"
                    readOnly={isLocked}
                  />
                </div>
                <div className={`facility-form-group facility-form-group--half ${isLocked ? 'facility-form-group--locked' : ''}`}>
                  <label className="facility-form-label">
                    Phone Number
                    <span className="facility-form-label__required">*</span>
                  </label>
                  <input
                    type="tel"
                    className="facility-form-input"
                    value={business.phone}
                    onChange={(e) => updateBusiness({ phone: e.target.value })}
                    placeholder="(405) 555-0100"
                    readOnly={isLocked}
                  />
                </div>
                <div className={`facility-form-group facility-form-group--half ${isLocked ? 'facility-form-group--locked' : ''}`}>
                  <label className="facility-form-label">
                    Website
                    <span className="facility-form-label__optional">(optional)</span>
                  </label>
                  <input
                    type="url"
                    className="facility-form-input"
                    value={business.website || ''}
                    onChange={(e) => updateBusiness({ website: e.target.value })}
                    placeholder="https://yourbusiness.com"
                    readOnly={isLocked}
                  />
                </div>
                <div className={`facility-form-group facility-form-group--full ${isLocked ? 'facility-form-group--locked' : ''}`}>
                  <label className="facility-form-label">
                    Description
                    <span className="facility-form-label__required">*</span>
                  </label>
                  <textarea
                    className="facility-form-textarea"
                    value={business.description}
                    onChange={(e) => updateBusiness({ description: e.target.value })}
                    placeholder="Tell customers about your facility, amenities, and what makes it special..."
                    rows={4}
                    readOnly={isLocked}
                  />
                  <span className="facility-form-hint">
                    This description appears on your facility page in the app
                  </span>
                </div>

                <div className={`facility-form-group facility-form-group--full ${isLocked ? 'facility-form-group--locked' : ''}`}>
                  <label className="facility-form-label">
                    Waiver PDF
                    <span className="facility-form-label__optional">(optional)</span>
                  </label>
                  <div className="facility-settings-card">
                    <label className="facility-form-label">Waiver handling</label>
                    <select className="facility-form-input" disabled={isLocked}
                      value={business.waiverPolicy?.method || 'unknown'}
                      onChange={e => updateBusiness({ waiverPolicy: { evidence: '', instructions: '', ...business.waiverPolicy, method: e.target.value as 'unknown' | 'at_venue' | 'checkout' | 'not_required' } })}>
                      <option value="unknown">Not verified (uploaded PDFs still require checkout signing)</option>
                      <option value="at_venue">Venue permits signing at arrival</option>
                      <option value="checkout">Customer signs uploaded PDF in PickleCourts checkout</option>
                      <option value="not_required">Venue confirms no waiver required</option>
                    </select>
                    <label className="facility-form-label">Verification source (staff only)</label>
                    <textarea className="facility-form-input" readOnly={isLocked} maxLength={2000}
                      value={business.waiverPolicy?.evidence || ''} placeholder="Policy URL or venue contact, confirmation and date"
                      onChange={e => updateBusiness({ waiverPolicy: { method: 'unknown', instructions: '', ...business.waiverPolicy, evidence: e.target.value } })} />
                    <label className="facility-form-label">Arrival instructions for customers</label>
                    <textarea className="facility-form-input" readOnly={isLocked} maxLength={1000}
                      value={business.waiverPolicy?.instructions || ''}
                      onChange={e => updateBusiness({ waiverPolicy: { method: 'unknown', evidence: '', ...business.waiverPolicy, instructions: e.target.value } })} />
                    <p className="facility-settings-card__hint">Use checkout signing only when the venue accepts the uploaded document signed through PickleCourts. A saved PDF does not authorize staff to impersonate a player in the venue checkout. Each participant or guardian may need their own signature.</p>
                    {business.waiverPdf?.url ? (
                      <>
                        <div className="facility-settings-card__row">
                          <div>
                            <p className="facility-settings-card__title">{business.waiverPdf.fileName || 'Waiver.pdf'}</p>
                            <p className="facility-settings-card__hint">Customers sign this PDF at checkout unless a verified arrival or no-waiver policy is selected. Each signature is bound to this document's contents.</p>
                            {(business.waiverPdf.sha256 || business.waiverPdf.uploadedAt) && (
                              <p className="facility-settings-card__hint" style={{ fontSize: 12, color: '#6b7280', marginTop: 6 }}>
                                {business.waiverPdf.uploadedAt && (
                                  <>Uploaded {new Date(business.waiverPdf.uploadedAt).toLocaleDateString()}</>
                                )}
                                {business.waiverPdf.uploadedAt && business.waiverPdf.sha256 && ' · '}
                                {business.waiverPdf.sha256 && (
                                  <>Document hash: <code style={{ fontFamily: 'monospace' }}>{business.waiverPdf.sha256.slice(0, 12)}…</code></>
                                )}
                              </p>
                            )}
                          </div>
                          <a
                            href={business.waiverPdf.url}
                            target="_blank"
                            rel="noreferrer"
                            className="facility-settings-card__link"
                          >
                            View PDF
                          </a>
                        </div>
                        {!isLocked && (
                          <>
                            <p className="facility-settings-card__hint" style={{ fontSize: 12, color: '#92400e', background: '#fef3c7', padding: '6px 10px', borderRadius: 6, marginTop: 10 }}>
                              Replacing this file invalidates prior signatures — every signer must re-sign on their next booking.
                            </p>
                            <div className="facility-settings-card__actions">
                              <button
                                type="button"
                                className="facility-settings-btn"
                                onClick={() => waiverInputRef.current?.click()}
                                disabled={waiverUploading}
                              >
                                {waiverUploading ? 'Uploading...' : 'Replace PDF'}
                              </button>
                              <button
                                type="button"
                                className="facility-settings-btn facility-settings-btn--danger"
                                onClick={handleRemoveWaiver}
                                disabled={waiverUploading}
                              >
                                Remove
                              </button>
                            </div>
                          </>
                        )}
                      </>
                    ) : (
                      <div className="facility-settings-card__row">
                        <p className="facility-settings-card__hint">Upload a waiver PDF that customers accept before paying.</p>
                        {!isLocked && (
                          <button
                            type="button"
                            className="facility-settings-btn"
                            onClick={() => waiverInputRef.current?.click()}
                            disabled={waiverUploading}
                          >
                            {waiverUploading ? 'Uploading...' : 'Upload PDF'}
                          </button>
                        )}
                      </div>
                    )}
                    <input
                      ref={waiverInputRef}
                      type="file"
                      accept="application/pdf"
                      onChange={handleWaiverFileSelect}
                      style={{ display: 'none' }}
                    />
                    {waiverUploadError && (
                      <p className="facility-settings-card__error">{waiverUploadError}</p>
                    )}
                  </div>
                </div>

                <div className={`facility-form-group facility-form-group--full ${isLocked ? 'facility-form-group--locked' : ''}`}>
                  <label className="facility-form-label">Equipment Rental at Checkout</label>
                  <div className="facility-settings-card">
                    <label className="facility-settings-toggle">
                      <input
                        type="checkbox"
                        checked={business.equipmentRental.enabled}
                        onChange={(e) => updateBusiness({
                          equipmentRental: {
                            ...business.equipmentRental,
                            enabled: e.target.checked,
                          },
                        })}
                        disabled={isLocked}
                      />
                      <span>Offer paddle/ball rentals during checkout</span>
                    </label>

                    {business.equipmentRental.enabled && (
                      <div className="facility-settings-grid">
                        <div className="facility-settings-field">
                          <label className="facility-form-label">Rental Label</label>
                          <input
                            type="text"
                            className="facility-form-input"
                            value={business.equipmentRental.label}
                            onChange={(e) => updateBusiness({
                              equipmentRental: {
                                ...business.equipmentRental,
                                label: e.target.value,
                              },
                            })}
                            placeholder="Paddle + Ball Rental"
                            readOnly={isLocked}
                          />
                        </div>
                        <div className="facility-settings-field">
                          <label className="facility-form-label">Price (USD)</label>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            className="facility-form-input"
                            value={(business.equipmentRental.priceCents / 100).toFixed(2)}
                            onChange={(e) => {
                              const dollars = Number(e.target.value);
                              updateBusiness({
                                equipmentRental: {
                                  ...business.equipmentRental,
                                  priceCents: Number.isFinite(dollars) ? Math.max(0, Math.round(dollars * 100)) : 0,
                                },
                              });
                            }}
                            readOnly={isLocked}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Location Section */}
        <div className={`facility-section ${expandedSections.has('location') ? 'facility-section--expanded' : ''}`}>
          <button className="facility-section__header" onClick={() => toggleSection('location')}>
            <div className="facility-section__header-left">
              <div className={`facility-section__icon ${isSectionComplete('location') ? 'facility-section__icon--complete' : ''}`}>
                {SECTION_CONFIG.location.icon}
              </div>
              <div className="facility-section__title-area">
                <h3 className="facility-section__title">{SECTION_CONFIG.location.title}</h3>
              </div>
            </div>
            <div className="facility-section__header-right">
              <span className={`facility-section__status ${isSectionComplete('location') ? 'facility-section__status--complete' : ''}`}>
                {isSectionComplete('location') ? (
                  <>
                    <svg viewBox="0 0 20 20" fill="currentColor" width="14" height="14">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    Complete
                  </>
                ) : 'Incomplete'}
              </span>
              <span className={`facility-section__chevron ${expandedSections.has('location') ? 'facility-section__chevron--expanded' : ''}`}>
                <svg viewBox="0 0 20 20" fill="currentColor" width="20" height="20">
                  <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </span>
            </div>
          </button>
          {expandedSections.has('location') && (
            <div className="facility-section__content">
              <div className="facility-form-grid">
                <div className={`facility-form-group facility-form-group--full ${isLocked ? 'facility-form-group--locked' : ''}`}>
                  <label className="facility-form-label">
                    Street Address
                    <span className="facility-form-label__required">*</span>
                  </label>
                  <input
                    type="text"
                    className="facility-form-input"
                    value={business.location.address}
                    onChange={(e) => updateBusiness({ location: { ...business.location, address: e.target.value } })}
                    placeholder="123 Main Street"
                    readOnly={isLocked}
                  />
                </div>
                <div className={`facility-form-group facility-form-group--third ${isLocked ? 'facility-form-group--locked' : ''}`}>
                  <label className="facility-form-label">
                    City
                    <span className="facility-form-label__required">*</span>
                  </label>
                  <input
                    type="text"
                    className="facility-form-input"
                    value={business.location.city}
                    onChange={(e) => updateBusiness({ location: { ...business.location, city: e.target.value } })}
                    placeholder="Oklahoma City"
                    readOnly={isLocked}
                  />
                </div>
                <div className={`facility-form-group facility-form-group--third ${isLocked ? 'facility-form-group--locked' : ''}`}>
                  <label className="facility-form-label">
                    State
                    <span className="facility-form-label__required">*</span>
                  </label>
                  <select
                    className="facility-form-select"
                    value={business.location.state}
                    onChange={(e) => updateBusiness({ location: { ...business.location, state: e.target.value } })}
                    disabled={isLocked}
                  >
                    <option value="OK">Oklahoma</option>
                    <option value="TX">Texas</option>
                    <option value="KS">Kansas</option>
                    <option value="AR">Arkansas</option>
                    <option value="MO">Missouri</option>
                    <option value="NE">Nebraska</option>
                    <option value="CO">Colorado</option>
                    <option value="NM">New Mexico</option>
                  </select>
                </div>
                <div className={`facility-form-group facility-form-group--third ${isLocked ? 'facility-form-group--locked' : ''}`}>
                  <label className="facility-form-label">
                    ZIP Code
                    <span className="facility-form-label__required">*</span>
                  </label>
                  <input
                    type="text"
                    className="facility-form-input"
                    value={business.location.zipCode}
                    onChange={(e) => updateBusiness({ location: { ...business.location, zipCode: e.target.value } })}
                    placeholder="73102"
                    readOnly={isLocked}
                  />
                </div>
              </div>
              <div className="facility-info-card">
                <svg viewBox="0 0 20 20" fill="currentColor" width="20" height="20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
                <p>Your location will be shown on the map when customers search for courts. Make sure your address is accurate for GPS directions.</p>
              </div>
            </div>
          )}
        </div>

        {/* Operating Hours Section */}
        <div className={`facility-section ${expandedSections.has('hours') ? 'facility-section--expanded' : ''}`}>
          <button className="facility-section__header" onClick={() => toggleSection('hours')}>
            <div className="facility-section__header-left">
              <div className={`facility-section__icon ${isSectionComplete('hours') ? 'facility-section__icon--complete' : ''}`}>
                {SECTION_CONFIG.hours.icon}
              </div>
              <div className="facility-section__title-area">
                <h3 className="facility-section__title">{SECTION_CONFIG.hours.title}</h3>
              </div>
            </div>
            <div className="facility-section__header-right">
              <span className={`facility-section__status ${isSectionComplete('hours') ? 'facility-section__status--complete' : ''}`}>
                {isSectionComplete('hours') ? (
                  <>
                    <svg viewBox="0 0 20 20" fill="currentColor" width="14" height="14">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    Complete
                  </>
                ) : 'Incomplete'}
              </span>
              <span className={`facility-section__chevron ${expandedSections.has('hours') ? 'facility-section__chevron--expanded' : ''}`}>
                <svg viewBox="0 0 20 20" fill="currentColor" width="20" height="20">
                  <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </span>
            </div>
          </button>
          {expandedSections.has('hours') && (
            <div className="facility-section__content">
              <div className="hours-grid">
                {DAYS_OF_WEEK.map((day, index) => {
                  const hours = business.operatingHours.find((h) => h.dayOfWeek === index);
                  const isClosed = hours?.isClosed || false;
                  const isWeekend = index === 0 || index === 6;

                  return (
                    <div key={day} className={`hours-row ${isClosed ? 'hours-row--closed' : ''} ${isWeekend ? 'hours-row--weekend' : ''} ${isLocked ? 'hours-row--locked' : ''}`}>
                      <div className="hours-row__day">
                        <span className="hours-row__day-name">{day}</span>
                        {isWeekend && <span className="hours-row__weekend-badge">Weekend</span>}
                      </div>
                      <div className="hours-row__times">
                        <div className="hours-row__time-input">
                          <label>Open</label>
                          <input
                            type="time"
                            value={hours?.openTime || '09:00'}
                            onChange={(e) => {
                              const newHours = business.operatingHours.map((h) =>
                                h.dayOfWeek === index ? { ...h, openTime: e.target.value } : h
                              );
                              updateBusiness({ operatingHours: newHours });
                            }}
                            disabled={isClosed || isLocked}
                          />
                        </div>
                        <span className="hours-row__separator">to</span>
                        <div className="hours-row__time-input">
                          <label>Close</label>
                          <input
                            type="time"
                            value={hours?.closeTime || '17:00'}
                            onChange={(e) => {
                              const newHours = business.operatingHours.map((h) =>
                                h.dayOfWeek === index ? { ...h, closeTime: e.target.value } : h
                              );
                              updateBusiness({ operatingHours: newHours });
                            }}
                            disabled={isClosed || isLocked}
                          />
                        </div>
                      </div>
                      <button
                        type="button"
                        className={`hours-row__toggle ${isClosed ? 'hours-row__toggle--closed' : 'hours-row__toggle--open'}`}
                        onClick={() => {
                          const newHours = business.operatingHours.map((h) =>
                            h.dayOfWeek === index ? { ...h, isClosed: !h.isClosed } : h
                          );
                          updateBusiness({ operatingHours: newHours });
                        }}
                        disabled={isLocked}
                      >
                        <span className="hours-row__toggle-knob" />
                        <span className="hours-row__toggle-text">{isClosed ? 'Closed' : 'Open'}</span>
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Photos Section */}
        <div className={`facility-section ${expandedSections.has('photos') ? 'facility-section--expanded' : ''}`}>
          <button className="facility-section__header" onClick={() => toggleSection('photos')}>
            <div className="facility-section__header-left">
              <div className={`facility-section__icon ${isSectionComplete('photos') ? 'facility-section__icon--complete' : ''}`}>
                {SECTION_CONFIG.photos.icon}
              </div>
              <div className="facility-section__title-area">
                <h3 className="facility-section__title">{SECTION_CONFIG.photos.title}</h3>
              </div>
            </div>
            <div className="facility-section__header-right">
              <span className="facility-section__count">{photos.length} photos</span>
              <span className={`facility-section__status ${isSectionComplete('photos') ? 'facility-section__status--complete' : ''}`}>
                {isSectionComplete('photos') ? (
                  <>
                    <svg viewBox="0 0 20 20" fill="currentColor" width="14" height="14">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    Complete
                  </>
                ) : 'Add photos'}
              </span>
              <span className={`facility-section__chevron ${expandedSections.has('photos') ? 'facility-section__chevron--expanded' : ''}`}>
                <svg viewBox="0 0 20 20" fill="currentColor" width="20" height="20">
                  <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </span>
            </div>
          </button>
          {expandedSections.has('photos') && (
            <div className="facility-section__content">
              {/* Upload target selector */}
              {!isLocked && (
                <div className="photo-upload-target">
                  <label className="facility-form-label">Upload photos to</label>
                  <div className="photo-target-options">
                    <button
                      type="button"
                      className={`photo-target-option ${selectedPhotoTarget === 'facility' ? 'photo-target-option--selected' : ''}`}
                      onClick={() => setSelectedPhotoTarget('facility')}
                    >
                      <svg viewBox="0 0 20 20" fill="currentColor" width="18" height="18">
                        <path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z" />
                      </svg>
                      Facility
                    </button>
                    {business.courts.map((court) => (
                      <button
                        key={court.id}
                        type="button"
                        className={`photo-target-option ${selectedPhotoTarget === court.id ? 'photo-target-option--selected' : ''}`}
                        onClick={() => setSelectedPhotoTarget(court.id)}
                      >
                        <svg viewBox="0 0 20 20" fill="currentColor" width="18" height="18">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                        </svg>
                        {court.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Upload dropzone */}
              {!isLocked && (
              <div
                className={`photo-dropzone ${dragActive ? 'photo-dropzone--active' : ''} ${uploading ? 'photo-dropzone--uploading' : ''}`}
                onClick={() => !uploading && fileInputRef.current?.click()}
                onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
                onDragLeave={() => setDragActive(false)}
                onDrop={handleDrop}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleFileSelect}
                  style={{ display: 'none' }}
                />
                <div className="photo-dropzone__icon">
                  {uploading ? (
                    <div className="photo-dropzone__spinner" />
                  ) : (
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <path d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  )}
                </div>
                {uploading ? (
                  <p className="photo-dropzone__text">Uploading photos...</p>
                ) : (
                  <>
                    <p className="photo-dropzone__text">
                      <span className="photo-dropzone__text--highlight">Click to upload</span> or drag and drop
                    </p>
                    <p className="photo-dropzone__hint">PNG, JPG, WEBP up to 10MB each</p>
                  </>
                )}
                {uploadError && (
                  <p className="photo-dropzone__error">{uploadError}</p>
                )}
              </div>
              )}

              {/* Photo gallery */}
              {facilityPhotos.length > 0 && (
                <div className="photo-gallery">
                  <h4 className="photo-gallery__title">
                    Facility Photos
                    <span className="photo-gallery__count">{facilityPhotos.length}</span>
                  </h4>
                  <div className="photo-gallery__grid">
                    {facilityPhotos.map((photo) => (
                      <div key={photo.id} className={`photo-card ${photo.isMain ? 'photo-card--main' : ''}`}>
                        <div className="photo-card__image-wrapper">
                          <img
                            src={photo.url}
                            alt={photo.name}
                            className="photo-card__image"
                          />
                          {photo.isMain && (
                            <div className="photo-card__main-badge">
                              <svg viewBox="0 0 20 20" fill="currentColor" width="12" height="12">
                                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                              </svg>
                              Main
                            </div>
                          )}
                          {!isLocked && (
                          <div className="photo-card__overlay">
                            {!photo.isMain && (
                              <button
                                type="button"
                                className="photo-card__action photo-card__action--star"
                                onClick={() => handleSetMainPhoto(photo.id)}
                                title="Set as main photo"
                              >
                                <svg viewBox="0 0 20 20" fill="currentColor" width="16" height="16">
                                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                </svg>
                              </button>
                            )}
                            <button
                              type="button"
                              className="photo-card__action photo-card__action--delete"
                              onClick={() => handleDeletePhoto(photo.id)}
                              title="Delete photo"
                            >
                              <svg viewBox="0 0 20 20" fill="currentColor" width="16" height="16">
                                <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                              </svg>
                            </button>
                          </div>
                          )}
                        </div>
                        <div className="photo-card__info">
                          <span className="photo-card__name">{photo.name}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Save Button */}
      {!isLocked && (
      <div className="facility-save-bar">
        <button
          className="facility-save-btn"
          onClick={handleSaveAll}
          disabled={saving || loading}
        >
          {saving ? (
            <>
              <span className="facility-save-btn__spinner" />
              Saving...
            </>
          ) : (
            <>
              <svg viewBox="0 0 20 20" fill="currentColor" width="18" height="18">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
              Save All Changes
            </>
          )}
        </button>
      </div>
      )}
    </DashboardLayout>
  );
}
